import React, { useEffect, useState } from "react";
import { HashLink } from "react-router-hash-link";
import { useLocation } from "react-router-dom";
import { useScreenTransition } from "../../Hooks/useScreenTransition";
import { SUPPORT_EMAIL, SUPPORT_PHONE, WHATSAPP_URL } from "../../utility/support";
import WhatsAppIcon from "../../NewComponents/WhatsAppIcon";
import "./site-footer.css";

/**
 * The site footer: brand and contact, link groups, legal and copyright.
 *
 * Same markup and stylesheet as the footer of the static pages
 * (tools/generate-seo-pages.js inlines site-footer.css), so a visitor moving
 * between the app and the static pages sees one footer.
 *
 * Every link is a real <a href>, not a <p onClick>: the public routes are
 * prerendered (tools/prerender.js) and a crawler only follows hrefs. Clicks
 * on app routes still go through transitionTo, so nothing reloads.
 *
 * Phones get the groups as a 2x2 grid of headings that open on tap; the
 * links stay in the DOM while a group is closed.
 */

const PHONE_QUERY = "(max-width: 719px)";

// Pages served as static HTML by Hosting, not React routes: they need a full
// page load (a React route for them would land on the SPA's 404).
export const CERTIFIED_FOR_GROUP = {
  title: "Certified for",
  items: [
    { title: "USCIS", path: "/uscis-certified-translation", external: true },
    { title: "Courts", path: "/court-certified-translation", external: true },
    { title: "Universities", path: "/university-certified-translation", external: true },
    { title: "University requirements", path: "/universities", external: true },
    { title: "Schools", path: "/school-certified-translation", external: true },
    { title: "Employers", path: "/employer-certified-translation", external: true },
    { title: "DMV", path: "/dmv-certified-translation", external: true },
  ],
};

export const DEFAULT_GROUPS = [
  {
    title: "Services",
    items: [
      { title: "Certified", path: "/certified" },
      { title: "Standard", path: "/standard" },
      { title: "Interpretation", path: "/interpretations" },
      { title: "For businesses", path: "/use-cases/business-document-translation-services", external: true },
      { title: "Pricing", path: "/#pricing", hashLink: true },
    ],
  },
  CERTIFIED_FOR_GROUP,
  {
    title: "Resources",
    items: [
      { title: "Documents", path: "/documents", external: true },
      { title: "Languages", path: "/languages" },
      { title: "Use cases", path: "/use-cases", external: true },
      { title: "Features", path: "/#features", hashLink: true },
      { title: "Blog", path: "/blog", external: true },
      { title: "Pricing", path: "/pricing", external: true },
      { title: "API and AI assistants", path: "/integrations", external: true },
    ],
  },
  {
    title: "Company",
    items: [
      { title: "About", path: "/about", external: true },
      { title: "Reviews", path: "/reviews" },
      { title: "Contact", path: "/contact" },
      // Pages for organisations: static pages, plain links (a crawler follows them).
      { title: "For law firms", path: "/for-law-firms", external: true },
      { title: "For family law firms", path: "/for-family-law-firms", external: true },
      { title: "For universities", path: "/for-universities", external: true },
      { title: "For schools and districts", path: "/for-schools-and-districts", external: true },
      { title: "Security", path: "/security", external: true },
    ],
  },
  {
    title: "Help",
    items: [
      { title: "FAQs", path: "/faq" },
      { title: "Doc verify", path: "/verify" },
    ],
  },
];

export const DEFAULT_LEGAL = [
  { title: "Privacy Policy", path: "/privacy" },
  { title: "Terms of Service", path: "/terms" },
  { title: "Refund Policy", path: "/refunds" },
];

const usePhone = () => {
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(PHONE_QUERY).matches
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(PHONE_QUERY);
    const onChange = () => setIsPhone(m.matches);
    onChange();
    if (m.addEventListener) m.addEventListener("change", onChange);
    else m.addListener(onChange);
    return () => (m.removeEventListener ? m.removeEventListener("change", onChange) : m.removeListener(onChange));
  }, []);
  return isPhone;
};

const isPlainClick = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const FooterLink = ({ item, onNavigate }) => {
  if (item.hashLink) {
    return <HashLink smooth to={item.path}>{item.title}</HashLink>;
  }
  if (item.external || item.href) {
    return <a href={item.href || item.path}>{item.title}</a>;
  }
  const href = item.id ? `${item.path}#${item.id}` : item.path;
  return (
    <a
      href={href}
      onClick={(e) => {
        if (!isPlainClick(e)) return;
        e.preventDefault();
        onNavigate(item, e);
      }}
    >
      {item.title}
    </a>
  );
};

const SiteFooter = ({ groups = DEFAULT_GROUPS, legal = DEFAULT_LEGAL, light = false, logoSrc, defaultPath = "/", className = "" }) => {
  const { transitionTo } = useScreenTransition();
  const { pathname } = useLocation();
  const isPhone = usePhone();
  const [open, setOpen] = useState({});

  // Items with an id are sections of the current landing page (/partner).
  const onNavigate = (item, e) => {
    if (pathname === defaultPath && item.id) {
      const el = document.getElementById(item.id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    transitionTo(item.path, e);
  };

  return (
    <footer className={`evf ${light ? "evf--light" : ""} ${className}`.trim()} style={{ "--evf-cols": Math.max(4, Math.min(groups.length, 5)) }}>
      <div className="evf-in">
        <div className="evf-brand">
          <a
            className="evf-logo"
            href="/"
            onClick={(e) => {
              if (!isPlainClick(e)) return;
              e.preventDefault();
              transitionTo("/?redirect=false", e);
            }}
          >
            {logoSrc ? (
              <img className="evf-wordmark" src={logoSrc} alt="eVerbary" loading="lazy" decoding="async" />
            ) : (
              <>
                <img src="/logo.png" alt="" width="32" height="32" loading="lazy" decoding="async" />
                eVerbary
              </>
            )}
          </a>
          <p className="evf-tag">
            Certified translations by professional human translators, accepted by USCIS, courts and universities.
          </p>
          <p className="evf-contact">
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            {/* WhatsApp only: nobody answers calls on this number. */}
            <a className="evf-wa" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon />WhatsApp {SUPPORT_PHONE}
            </a>
          </p>
        </div>
        <nav className="evf-groups" aria-label="Footer">
          {groups.map((g) => (
            <details key={g.title} className="evf-g" open={!isPhone || !!open[g.title]}>
              <summary
                onClick={(e) => {
                  e.preventDefault();
                  if (isPhone) setOpen((o) => ({ ...o, [g.title]: !o[g.title] }));
                }}
              >
                {g.title}
              </summary>
              <ul>
                {g.items.map((item) => (
                  <li key={`${item.title}-${item.path}`}>
                    <FooterLink item={item} onNavigate={onNavigate} />
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </nav>
        <div className="evf-bottom">
          <p>&copy; {new Date().getFullYear()} eVerbary. All rights reserved.</p>
          <p className="evf-legal">
            {legal.map((item) => (
              <FooterLink key={item.path} item={item} onNavigate={onNavigate} />
            ))}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
